import { RangeEntryWrapper } from '../../Types'
import { IsVarEmpty, isVarNotEmpty } from '../../../../../../../NonReact/utils/GlobalFunctions'
import { COMMA, DASH, EMPTY_STRING, EXCLUDE } from '../../../../../../../NonReact/constants/Common'
import { $debug } from '../../../../../Utils/ConsoleUtils'
import { StringUtils } from '../../../../../../../NonReact/utils/StringUtils'
import { combineOverlappingOrSequentialRanges } from '../CombineOverlappingOrSequentialRanges/CombineOverlappingOrSequentialRanges'
import { combineSequentialNumbersIntoRanges } from '../CombineSequentialNumbersIntoRanges/CombineSequentialNumbersIntoRanges'
import { dedupeAndCleanRangeEntryWrappers } from '../DepdupeAndCleanRangeEntryWapper/DedupeAndCleanRangeEntryWrappers'
import { normaliseDashes } from '../Utils/Utilities'

const RANGE_DEBUG: string = 'Invalid range format. Expected format: "1-2" or "1"'

/**
 * Function to replace cedilla and en dash with comma and dash
 *
 * @param value - The value to replace
 *
 * @returns The replaced value
 */
const replaceCedillaAndEnDash = (value: string): string =>
    normaliseDashes(value)
        .replace(/\u00B8/g, COMMA)
        .replace(EXCLUDE, EMPTY_STRING)

/**
 * Function to convert a string to a list of range wrappers
 *
 * @param value - The string value
 * @param validation - Object Validation
 *
 * @returns The list of range wrappers
 */
export const stringToRangeWrappers = (
    value: string,
    handleValidation?: (value: string) => boolean,
    updateValidationState?: (newValue: boolean) => void
): Nullable<RangeEntryWrapper[]> => {
    if (IsVarEmpty(value)) {
        return null
    }

    const combinedRanges: Nullable<RangeEntryWrapper>[] = value
        .split(COMMA)
        .map((range: string): Nullable<RangeEntryWrapper> => {
            const rangeParts: string[] = range.split(DASH)

            if (rangeParts.length === 0) {
                $debug(RANGE_DEBUG)

                return null
            }

            const isExclude: boolean = rangeParts[0].startsWith(EXCLUDE)

            if (rangeParts.length > 2) {
                return {
                    included: !isExclude,
                    isRange: false,
                    displayValue: replaceCedillaAndEnDash(range),
                    value: replaceCedillaAndEnDash(range)
                }
            }

            const startNumber: Nullable<number | string> = StringUtils.convertToNumberIfPossible(
                rangeParts[0].replace(EXCLUDE, EMPTY_STRING)
            )

            if (rangeParts.length === 2) {
                const [start, end]: string[] = rangeParts
                const endNumber: Nullable<number | string> = StringUtils.convertToNumberIfPossible(end)

                if (typeof startNumber === 'string' || typeof endNumber === 'string') {
                    return {
                        included: !isExclude,
                        isRange: false,
                        displayValue: replaceCedillaAndEnDash(range),
                        value: replaceCedillaAndEnDash(range)
                    }
                }

                if (IsVarEmpty(startNumber) || IsVarEmpty(endNumber) || (startNumber ?? 0) > (endNumber ?? 0)) {
                    $debug(RANGE_DEBUG)

                    return null
                }

                return {
                    included: !isExclude,
                    isRange: true,
                    value: {
                        minDisplayValue: replaceCedillaAndEnDash(start),
                        min: startNumber,
                        maxDisplayValue: replaceCedillaAndEnDash(end),
                        max: endNumber
                    }
                }
            }

            if (IsVarEmpty(startNumber)) {
                $debug(RANGE_DEBUG)

                return null
            }

            let validationError: boolean = false

            const newRange: RangeEntryWrapper = {
                included: !isExclude,
                isRange: false,
                displayValue: replaceCedillaAndEnDash(range),
                value: typeof startNumber === 'string' ? replaceCedillaAndEnDash(startNumber) : startNumber
            }

            if (isVarNotEmpty(handleValidation)) {
                validationError = !handleValidation(startNumber.toString())

                newRange.validationError = validationError
            }

            return newRange
        })
        .filter((range: Nullable<RangeEntryWrapper>): boolean => isVarNotEmpty(range)) as RangeEntryWrapper[]

    if (isVarNotEmpty(handleValidation)) {
        const allValues: string[] = combinedRanges.map(
            (validationValue: Nullable<RangeEntryWrapper>) =>
                (validationValue?.value as string).toString() ?? EMPTY_STRING
        )
        const errors: boolean = allValues.some((validationValue: string) => !handleValidation(validationValue))

        if (errors && isVarNotEmpty(updateValidationState)) updateValidationState(true)
    }

    return combineOverlappingOrSequentialRanges(
        combineSequentialNumbersIntoRanges(dedupeAndCleanRangeEntryWrappers(combinedRanges))
    )
}
